/**
 * Import Data Service
 * Handles uploading Excel sheet data to Cosmic Frog tables
 */

import { getUserAppKey } from './optilogicService';

const API_BASE_URL = 'https://api-dev.cosmicfrog.optilogic.app/tadpole/cosmicfrog/v0.2/anura';
const SCHEMA = 'anura_2_8';

/**
 * Convert 2D array to CSV string
 * @param {Array<Array>} data - 2D array of data from Excel
 * @returns {string} CSV formatted string
 */
function convertToCSV(data) {
    if (!data || data.length === 0) {
        return '';
    }

    const csvRows = data.map(row => {
        // Handle each cell value
        return row.map(cell => {
            // Convert cell to string
            let cellValue = cell === null || cell === undefined ? '' : String(cell);

            // Escape quotes by doubling them
            cellValue = cellValue.replace(/"/g, '""');

            // Wrap in quotes if contains comma, newline, or quote
            if (cellValue.includes(',') || cellValue.includes('\n') || cellValue.includes('"')) {
                cellValue = `"${cellValue}"`;
            }

            return cellValue;
        }).join(',');
    });

    return csvRows.join('\n');
}

/**
 * Upload table data from Excel sheet to Cosmic Frog
 *
 * @param {string} modelId - The Cosmic Frog model ID
 * @param {string} tableName - The table name to import into
 * @param {string} sheetName - The Excel sheet name to read from
 * @param {string} upsertOrReplace - Either "Upsert" or "Replace"
 * @returns {Promise<object>} Response from the API
 */
export async function uploadTableData(modelId, tableName, sheetName, upsertOrReplace) {
    const token = getUserAppKey();

    if (!token) {
        throw new Error('Not authenticated. Please log in first.');
    }

    // Read data from Excel sheet
    let sheetData;
    try {
        sheetData = await readSheetData(sheetName);
    } catch (error) {
        throw new Error(`Failed to read sheet "${sheetName}": ${error.message}`);
    }

    if (!sheetData || sheetData.length === 0) {
        throw new Error(`Sheet "${sheetName}" is empty or has no data.`);
    }

    // Convert to CSV string
    const csvString = convertToCSV(sheetData);

    // Create blob from CSV string
    const blob = new Blob([csvString], { type: 'text/csv' });

    // Create FormData
    const formData = new FormData();
    const fileName = `${tableName}.csv`;
    formData.append('file', blob, fileName);
    formData.append('name', fileName);
    formData.append('modelId', modelId);
    formData.append('tableName', tableName);

    // Determine overwrite parameter (Replace = true, Upsert = false)
    const overwrite = upsertOrReplace === 'Replace';

    // Build URL
    const url = `${API_BASE_URL}/${encodeURIComponent(modelId)}/${encodeURIComponent(tableName)}/upload-file?overwrite=${overwrite}`;

    // Make API call
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'X-APP-KEY': token,
            'Authorization': `Bearer ${token}`,
            'schema': SCHEMA
        },
        body: formData
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();
    return {
        success: true,
        message: result || 'Data uploaded successfully',
        rowCount: sheetData.length - 1 // Subtract header row
    };
}

/**
 * Read all data from an Excel sheet
 * @param {string} sheetName - Name of the sheet to read
 * @returns {Promise<Array<Array>>} 2D array of sheet data
 */
async function readSheetData(sheetName) {
    return await window.Excel.run(async (context) => {
        const workbook = context.workbook;
        const sheet = workbook.worksheets.getItem(sheetName);
        const usedRange = sheet.getUsedRange();

        usedRange.load('values');
        await context.sync();

        return usedRange.values;
    });
}

/**
 * Get preview of data that will be uploaded
 * @param {string} sheetName - Name of the sheet
 * @param {number} maxRows - Maximum rows to preview (default 5)
 * @returns {Promise<object>} Preview data with row count
 */
export async function getUploadPreview(sheetName, maxRows = 5) {
    const data = await readSheetData(sheetName);

    return {
        totalRows: data.length - 1, // Subtract header
        headers: data[0],
        previewRows: data.slice(1, Math.min(maxRows + 1, data.length)),
        hasMoreRows: data.length > maxRows + 1
    };
}
